/** Transcript scroll math + reusable masked viewport scrolling. */

import Phaser from "phaser";

import {
  clampScroll,
  lerpScrollY,
  npcDialogContainerY,
  scrollMaxForContent,
  scrollYForLineBottom,
} from "./npc-dialog-scroll-math.js";

export {
  clampScroll,
  npcDialogContainerY,
  scrollMaxForContent,
  scrollYForLineBottom,
} from "./npc-dialog-scroll-math.js";

export function measureContainerContentHeight(
  container: Phaser.GameObjects.Container,
  lineGap: number,
  topPad = 0
): number {
  const children = container.list as Phaser.GameObjects.Text[];
  if (children.length === 0) return topPad;
  let y = topPad;
  for (let i = 0; i < children.length; i++) {
    const child = children[i]!;
    const h = child.height;
    if (Number.isFinite(h) && h > 0) {
      y += h;
      if (i < children.length - 1) y += lineGap;
    }
  }
  return y + 8;
}

export type MaskedScrollBindings = {
  scene: Phaser.Scene;
  hitTarget: Phaser.GameObjects.Rectangle;
  getContainer: () => Phaser.GameObjects.Container | null;
  getViewportTop: () => number;
  getViewportH: () => number;
  getLineGap: () => number;
  onApply?: () => void;
};

/** Wheel + touch drag scroll for a masked Phaser text container. */
export class MaskedScrollViewport {
  scrollY = 0;
  scrollMax = 0;
  stickToBottom = true;

  private dragStartY: number | null = null;
  private dragScrollStart = 0;
  private wheelHandler:
    | ((
        pointer: Phaser.Input.Pointer,
        gameObjects: Phaser.GameObjects.GameObject[],
        deltaX: number,
        deltaY: number
      ) => void)
    | null = null;
  private readonly onPointerMove: (pointer: Phaser.Input.Pointer) => void;
  private readonly onPointerUp: () => void;

  constructor(private readonly bindings: MaskedScrollBindings) {
    this.onPointerMove = (pointer: Phaser.Input.Pointer): void => {
      if (this.dragStartY === null) return;
      const dy = pointer.y - this.dragStartY;
      this.scrollY = clampScroll(this.dragScrollStart - dy, this.scrollMax);
      this.applyScroll();
    };
    this.onPointerUp = (): void => {
      this.dragStartY = null;
    };
  }

  bind(): void {
    this.unbind();
    const { scene, hitTarget } = this.bindings;
    this.wheelHandler = (
      _pointer: Phaser.Input.Pointer,
      _go: Phaser.GameObjects.GameObject[],
      _dx: number,
      dy: number
    ) => {
      if (!this.pointerInHit()) return;
      this.stickToBottom = false;
      this.scrollBy(dy * 0.35);
    };
    scene.input.on("wheel", this.wheelHandler as (...args: unknown[]) => void);
    hitTarget.setInteractive({ useHandCursor: true });
    hitTarget.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      this.dragStartY = pointer.y;
      this.dragScrollStart = this.scrollY;
      this.stickToBottom = false;
    });
    scene.input.on("pointermove", this.onPointerMove);
    scene.input.on("pointerup", this.onPointerUp);
  }

  unbind(): void {
    const { scene, hitTarget } = this.bindings;
    if (this.wheelHandler) {
      scene.input.off("wheel", this.wheelHandler as (...args: unknown[]) => void);
      this.wheelHandler = null;
    }
    scene.input.off("pointermove", this.onPointerMove);
    scene.input.off("pointerup", this.onPointerUp);
    hitTarget.disableInteractive();
    this.dragStartY = null;
  }

  reflow(opts?: { forceBottom?: boolean }): void {
    const container = this.bindings.getContainer();
    if (!container) return;
    const contentH = measureContainerContentHeight(
      container,
      this.bindings.getLineGap()
    );
    this.scrollMax = scrollMaxForContent(contentH, this.bindings.getViewportH());
    const nearBottom = this.scrollMax <= 0 || this.scrollY >= this.scrollMax - 8;
    if (opts?.forceBottom || this.stickToBottom || nearBottom) {
      this.scrollY = this.scrollMax;
    } else {
      this.scrollY = clampScroll(this.scrollY, this.scrollMax);
    }
    this.applyScroll();
  }

  scrollToKeepLineBottom(lineBottomLocal: number): void {
    const container = this.bindings.getContainer();
    if (!container) return;
    this.scrollMax = scrollMaxForContent(
      measureContainerContentHeight(container, this.bindings.getLineGap()),
      this.bindings.getViewportH()
    );
    const target = scrollYForLineBottom(
      lineBottomLocal,
      this.bindings.getViewportH(),
      this.scrollMax
    );
    if (this.stickToBottom && this.scrollMax > 0) {
      this.scrollY = lerpScrollY(this.scrollY, target, 0.42);
    } else if (this.stickToBottom) {
      this.scrollY = target;
    } else {
      this.scrollY = clampScroll(this.scrollY, this.scrollMax);
    }
    this.applyScroll();
  }

  scrollBy(delta: number): void {
    this.scrollY = clampScroll(this.scrollY - delta, this.scrollMax);
    this.applyScroll();
  }

  applyScroll(): void {
    const container = this.bindings.getContainer();
    if (!container) return;
    const y = npcDialogContainerY({
      viewportY: this.bindings.getViewportTop(),
      scrollY: this.scrollY,
      scrollMax: this.scrollMax,
    });
    container.setY(y);
    this.bindings.onApply?.();
  }

  private pointerInHit(): boolean {
    const p = this.bindings.scene.input.activePointer;
    const b = this.bindings.hitTarget.getBounds();
    return b.contains(p.x, p.y);
  }
}
